import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { usersService } from '../../services/firestoreService';
import { activityLogService } from '../../services/activityLogService';
import './ThemeSelector.css';

const themes = [
  {
    id: 'default',
    name: '🌈 Default',
    colors: {
      primary: '#667eea',
      secondary: '#764ba2',
      background: '#f5f7fa',
      accent: '#4facfe'
    }
  },
  {
    id: 'ocean',
    name: '🌊 Ocean',
    colors: {
      primary: '#2196F3',
      secondary: '#03A9F4',
      background: '#E3F2FD',
      accent: '#00BCD4'
    }
  },
  {
    id: 'forest',
    name: '🌲 Forest',
    colors: {
      primary: '#4CAF50',
      secondary: '#8BC34A',
      background: '#E8F5E9',
      accent: '#66BB6A'
    }
  },
  {
    id: 'sunset',
    name: '🌅 Sunset',
    colors: {
      primary: '#FF5722',
      secondary: '#FF9800',
      background: '#FFF3E0',
      accent: '#FF6B35'
    }
  },
  {
    id: 'purple',
    name: '💜 Purple',
    colors: {
      primary: '#9C27B0',
      secondary: '#BA68C8',
      background: '#F3E5F5',
      accent: '#CE93D8'
    }
  },
  {
    id: 'pink',
    name: '🌸 Pink',
    colors: {
      primary: '#E91E63',
      secondary: '#F06292',
      background: '#FCE4EC',
      accent: '#F48FB1'
    }
  },
  {
    id: 'space',
    name: '🚀 Space',
    colors: {
      primary: '#673AB7',
      secondary: '#3F51B5',
      background: '#1A1A2E',
      accent: '#0F3460'
    }
  },
  {
    id: 'candy',
    name: '🍭 Candy',
    colors: {
      primary: '#FF4081',
      secondary: '#FF6EC7',
      background: '#FFF0F5',
      accent: '#FFB6C1'
    }
  }
];

const ThemeSelector = ({ onThemeSelect = null }) => {
  const { user } = useUser();
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadTheme = async () => {
      if (user?.id) {
        try {
          const userData = await usersService.getUser(user.id);
          if (userData?.theme) {
            setSelectedTheme(userData.theme);
            applyTheme(userData.theme);
          } else {
            // Apply default theme if no preference found
            applyTheme('default');
          }
        } catch (error) {
          console.debug('No theme preference found');
          // Apply default theme on error
          applyTheme('default');
        }
      } else {
        // Apply default theme if user not logged in
        applyTheme('default');
      }
    };
    loadTheme();
  }, [user]);

  const applyTheme = (themeId) => {
    const theme = themes.find(t => t.id === themeId) || themes[0];
    const root = document.documentElement;
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-secondary', theme.colors.secondary);
    root.style.setProperty('--theme-background', theme.colors.background);
    root.style.setProperty('--theme-accent', theme.colors.accent);
    
    // Also update body background directly for immediate visual feedback
    document.body.style.background = `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`;
  };

  const handleThemeSelect = async (themeId) => {
    // Apply theme immediately for instant feedback
    setSelectedTheme(themeId);
    applyTheme(themeId);
    
    // Force a re-render of the body background
    document.body.style.background = `linear-gradient(135deg, ${themes.find(t => t.id === themeId)?.colors.primary || themes[0].colors.primary} 0%, ${themes.find(t => t.id === themeId)?.colors.secondary || themes[0].colors.secondary} 100%)`;
    
    if (user?.id) {
      setSaving(true);
      try {
        await usersService.updateUser(user.id, { theme: themeId });
        // Log theme change activity
        await activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.THEME_CHANGE, {
          themeId
        });
        if (onThemeSelect) {
          onThemeSelect(themeId);
        }
      } catch (error) {
        console.error('Error saving theme:', error);
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <div className="theme-selector">
      <h3>🎨 Choose Your Theme</h3>
      <p className="theme-description">Pick a color theme that makes learning fun!</p>
      <div className="themes-grid">
        {themes.map((theme) => (
          <div
            key={theme.id}
            className={`theme-card ${selectedTheme === theme.id ? 'selected' : ''}`}
            onClick={() => handleThemeSelect(theme.id)}
            style={{
              '--theme-primary': theme.colors.primary,
              '--theme-secondary': theme.colors.secondary,
              '--theme-background': theme.colors.background,
              '--theme-accent': theme.colors.accent
            }}
          >
            <div className="theme-preview">
              <div className="preview-color" style={{ background: theme.colors.primary }}></div>
              <div className="preview-color" style={{ background: theme.colors.secondary }}></div>
              <div className="preview-color" style={{ background: theme.colors.accent }}></div>
            </div>
            <div className="theme-name">{theme.name}</div>
            {selectedTheme === theme.id && (
              <div className="selected-indicator">✓</div>
            )}
          </div>
        ))}
      </div>
      {saving && <p className="saving-indicator">💾 Saving...</p>}
    </div>
  );
};

export default ThemeSelector;

